import { usePosMode } from '@/features/pos/contexts/PosMode'
import { PosMode } from '@/features/pos/types'
import StorageService from '@/services/storage'
import { FC, ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react'
import invariant from 'tiny-invariant'

type CartKey = string | null | undefined

type CartKeyContextValue = {
    cartKey: CartKey
    mode: PosMode
    setCartKey: (cartKey: string) => void
    clearCartKey: () => void
}

const CartKeyContext = createContext<CartKeyContextValue | undefined>(undefined)

interface CartKeyAndMode {
    cartKey: CartKey
    mode: PosMode
}

interface ModeCartKeys {
    pos: CartKey
    phone: CartKey
}

interface CartKeyProviderProps {
    children: ReactNode
}

const CartKeyProvider: FC<CartKeyProviderProps> = ({ children }) => {
    const mode = usePosMode()

    const [activeCartKeyAndMode, setActiveCartKeyAndMode] = useState<CartKeyAndMode>({
        cartKey: undefined,
        mode,
    })

    const cartKeysRef = useRef<ModeCartKeys>({ pos: undefined, phone: undefined })

    useEffect(() => {
        const handleSetCartKey = async () => {
            const [posCartKey, phoneCartKey] = await Promise.all([
                StorageService.getPosCartKey(),
                StorageService.getPhoneCartKey(),
            ])

            cartKeysRef.current = { pos: posCartKey, phone: phoneCartKey }

            setActiveCartKeyAndMode(({ mode }) => ({
                cartKey: mode === 'pos' ? posCartKey : phoneCartKey,
                mode,
            }))
        }

        handleSetCartKey()
    }, [])

    useEffect(() => {
        setActiveCartKeyAndMode({ cartKey: cartKeysRef.current[mode], mode })
    }, [mode])

    const setCartKey = (cartKey: string) => {
        setActiveCartKeyAndMode({ cartKey, mode })

        if (mode === 'pos') {
            cartKeysRef.current.pos = cartKey

            StorageService.setPosCartKey(cartKey)
        } else {
            cartKeysRef.current.phone = cartKey

            StorageService.setPhoneCartKey(cartKey)
        }
    }

    const clearCartKey = () => {
        setActiveCartKeyAndMode({ cartKey: null, mode })

        if (mode === 'pos') {
            cartKeysRef.current.pos = null

            void StorageService.removePosCartKey()
        } else {
            cartKeysRef.current.phone = null

            void StorageService.removePhoneCartKey()
        }
    }

    return (
        <CartKeyContext.Provider
            value={{
                ...activeCartKeyAndMode,
                setCartKey,
                clearCartKey,
            }}
        >
            {children}
        </CartKeyContext.Provider>
    )
}

export default CartKeyProvider

export const useCartKeyContext = () => {
    const context = useContext(CartKeyContext)

    invariant(context, 'useCartKeyContext must be used within a CartKeyProvider')

    return context
}
